const React = require("react");
const { MdCheckCircle, MdCancel } = require("react-icons/md");
const { ShippingCostCalculator } = require("../ShippingCostCalculator");
const h = React.createElement;

const money = (value) => `$${(Number(value) || 0).toFixed(2)}`;
const isNumeric = (value) => typeof value === "number" && Number.isFinite(value);

// Vert de plus en plus intense selon le profit ($1 -> niveau 1, $10+ ->
// niveau 10) -- niveaux 1-5 gardent un texte foncé, 6-10 basculent en blanc
// pour rester lisibles sur un fond plus saturé.
const GREEN_LEVELS = ["#eafaf1", "#d7f5e4", "#c2f0d6", "#a8e8c3", "#8fdfb0", "#6bd699", "#4ecb85", "#2ebd6d", "#17ab5a", "#0b8f47"];
const profitLevel = (profit) => Math.min(10, Math.max(1, Math.ceil(profit)));

// Étape 2 du CJSyncModal. Ne persiste jamais rien (aucun import.../update...)
// -- calcule uniquement, en local, via ShippingCostCalculator (moteur
// calculateCJPricing, seule source de vérité des calculs de prix CJ, voir ce
// fichier). `onPricingResult` remonte à CJSyncModal le payload prêt pour
// Finalize Synchronization (jamais appliqué avant ce clic-là), `onValidChange`
// prévient le stepper/footer.
// `initialConfig` (Resync): the product's current dropshippingData, used to
// pre-fill countries, the global variant selection and country prices.
const CJShippingPricingStep = ({ dropshippingProductId, costPrice, onValidChange, onPricingResult, t, initialConfig = null }) => {
  const [shippingMode, setShippingMode] = React.useState("include"); // "include" | "separate", "include" par défaut (voir demande)
  const [pricing, setPricing] = React.useState(null);

  const handlePricingChange = React.useCallback((data) => setPricing(data), []);

  // calculateCJPricing (ShippingCostCalculator.js) retourne `profit` comme un
  // number simple -- plus besoin de coercion défensive ici (l'ancien
  // calProfit d'allPriceFeet.jsx renvoyait tantôt une string, tantôt un
  // number selon la branche, ce qui masquait silencieusement un bug de
  // validation ; ce moteur central n'a plus cette incohérence).
  const netProfit = pricing?.pricing ? pricing.pricing.profit : null;
  const hasSimulation = Boolean(pricing?.ready && pricing.pricing);
  // product.salePrice (discount %) : 0 = pas de remise (toujours permis),
  // sinon minimum 3% -- déjà reflété dans pricing.ready (voir
  // ShippingCostCalculator's onPricingChange), gardé ici explicitement pour
  // que la condition soit lisible sans remonter dans l'autre fichier.
  const discountInvalid = Boolean(pricing?.discountInvalid);

  // Every ENABLED destination country must be profitable on its own (a
  // profitable United States never makes an unprofitable Canada valid) --
  // the Seller can disable a destination instead.
  const countryResults = pricing?.countryResults || [];
  const losingCountries = countryResults.filter((c) => !(isNumeric(c.profit) && c.profit > 0)).map((c) => c.countryCode);
  const valid = Boolean(
    hasSimulation
    && !discountInvalid
    && isNumeric(pricing.pricing.sellingPrice) && pricing.pricing.sellingPrice > 0
    && isNumeric(netProfit) && netProfit > 0
    && countryResults.length > 0
    && losingCountries.length === 0
  );

  React.useEffect(() => {
    onValidChange && onValidChange(valid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valid]);

  // coût total fournisseur (Product.dropshippingData.totalSupplierCost) :
  // "coût produit seul" si Separate Shipping, "coût produit + shipping" si
  // Include Shipping (voir commentaire sur DropshippingDataSchema,
  // server_elakay/elakay_models/product.js) -- se déduit directement de
  // calculateCJPricing sans branche séparée : cjProductCost + cjShippingCost
  // (coût réel total payé à CJ, toujours) moins shippingChargedToCustomer
  // (ce qui est déjà recouvré via la ligne shipping du checkout, 0 en
  // "include", = cjShippingCost en "separate").
  const totalSupplierCost = pricing?.pricing
    ? pricing.pricing.cjProductCost + pricing.pricing.cjShippingCost - pricing.pricing.shippingChargedToCustomer
    : 0;

  React.useEffect(() => {
    if (!valid) { onPricingResult && onPricingResult(null); return; }
    const p = pricing.pricing;
    const profitMargin = p.sellingPrice > 0 ? Number(((p.profit / p.sellingPrice) * 100).toFixed(2)) : 0;
    // Product.shipping = "free shipping" (voir le switch "Free Shipping" de
    // addProduct.jsx/editProduct.jsx, pas "a une livraison séparée") --
    // "include" (coût déjà dans le prix, le buyer ne paie rien de plus) =
    // shipping libre = true ; "separate" (buyer paie en plus au checkout) =
    // pas libre = false. Product.shippingPrice (racine, distinct de
    // dropshippingData.shippingPrice ci-dessous qui reste la source vive CJ)
    // = shippingChargedToCustomer directement (0 en "include", coût réel en
    // "separate") -- c'est exactement ce que le moteur calcule déjà.
    // pricing.selectedShipping is the CJ carrier/delivery quote the seller
    // picked (ShippingCostCalculator.js) -- previously only its cost total
    // (p.cjShippingCost) survived past this step; the carrier name, delivery
    // window and CJ's own taxes/clearance fees were computed, shown in the
    // radio list, then discarded. Kept here so order fulfillment (which
    // live-populates product.dropshippingData, see
    // dropShipping/CJDropshipping/services/orderFulfillment.service.js) can
    // read them too.
    const selected = pricing.selectedShipping || {};
    onPricingResult && onPricingResult({
      shippingMode,
      // ONE global variant selection (same for every country) -- applied by
      // the sync call at Finalize, never a Product field of updateCJPricing.
      variantSelection: pricing.variantSelection || null,
      price: p.sellingPrice,
      costPrice: totalSupplierCost,
      salePrice: p.discountPercent,
      shipping: shippingMode === "include",
      shippingPrice: p.shippingChargedToCustomer,
      dropshippingData: {
        shippingPrice: p.cjShippingCost,
        totalSupplierCost,
        paymentFees: p.stripeFee,
        elakayFees: p.elakayFee,
        totalFees: p.totalFees,
        calculatedProfit: p.profit,
        profitMargin,
        pricingMode: pricing.priceMode,
        // Raw "include"/"separate" choice, kept alongside the top-level
        // `shipping` boolean (product.shipping) so fulfillment/order code
        // doesn't have to re-derive the seller's intent from that boolean.
        shippingMode,
        shippingQuote: {
          carrier: selected.carrier || "",
          carrierCode: selected.carrierCode || "",
          carrierId: selected.carrierId || "",
          quotedVariantIds: Array.isArray(pricing.destinations?.[0]?.quotedVariantIds) ? pricing.destinations[0].quotedVariantIds : [],
          price: Number(selected.price) || 0,
          priceCny: Number(selected.priceCny) || 0,
          deliveryDays: selected.deliveryDays || "",
          taxesFee: Number(selected.taxesFee) || 0,
          clearanceOperationFee: Number(selected.clearanceOperationFee) || 0,
          totalPostageFee: Number(selected.totalPostageFee) || 0,
          destinationCountry: pricing.destinationCountry || "",
          quantity: Number(pricing.quantity) || 1,
        },
        // Every destination country with its own method/cost/delivery/
        // selling price/sellActive (shippingQuote above = the primary one).
        shippingDestinations: pricing.destinations || [],
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valid, pricing, shippingMode, totalSupplierCost]);

  const profitMarginDisplay = hasSimulation && pricing.pricing.sellingPrice > 0
    ? ((netProfit / pricing.pricing.sellingPrice) * 100).toFixed(1)
    : "0.0";
  const discountAmount = hasSimulation ? pricing.pricing.sellingPrice - pricing.pricing.chargedPrice : 0;

  const summaryStateClass = !hasSimulation
    ? ""
    : netProfit > 0
      ? "cjwiz-summary-card--profit"
      : "cjwiz-summary-card--loss";
  const summaryStyle = hasSimulation && netProfit > 0
    ? { background: GREEN_LEVELS[profitLevel(netProfit) - 1], color: profitLevel(netProfit) > 5 ? "#fff" : "#0f5132" }
    : undefined;

  return h("div", { className: "cjwiz-step" },
    h("div", { className: "cjwiz-step__intro" },
      h("h3", null, t("h_cjwiz_step2_title", "Shipping & Pricing")),
      h("p", { className: "cjds-muted" }, t(
        "h_cjwiz_step2_desc",
        "The CJ shipping cost is calculated automatically below. Use Refresh if you change the destination, variant or quantity."
      ))
    ),

    h("div", { className: "cjwiz-shipping-mode" },
      h("h4", { className: "cjwiz-subheading" }, t("h_cjwiz_shipmode_title", "Shipping mode")),
      h("div", { className: "cjwiz-mode-cards" },
        h("label", { className: `cjwiz-mode-card${shippingMode === "include" ? " cjwiz-mode-card--active" : ""}` },
          h("input", { type: "radio", name: "cjwiz-shipping-mode", checked: shippingMode === "include", onChange: () => setShippingMode("include") }),
          h("div", null,
            h("strong", null, t("h_cjwiz_shipmode_include_title", "Include Shipping in Product Price")),
            h("span", { className: "cjwiz-badge cjwiz-badge--muted" }, t("h_cjwiz_shipmode_default", "Default")),
            h("p", null, t("h_cjwiz_shipmode_include_desc", "The buyer never sees a separate shipping charge -- it's already folded into the Elakay price."))
          )
        ),
        h("label", { className: `cjwiz-mode-card${shippingMode === "separate" ? " cjwiz-mode-card--active" : ""}` },
          h("input", { type: "radio", name: "cjwiz-shipping-mode", checked: shippingMode === "separate", onChange: () => setShippingMode("separate") }),
          h("div", null,
            h("strong", null, t("h_cjwiz_shipmode_separate_title", "Separate Shipping")),
            h("p", null, t("h_cjwiz_shipmode_separate_desc", "The buyer pays shipping separately at checkout, on top of the product price."))
          )
        )
      )
    ),

    h(ShippingCostCalculator, {
      productId: dropshippingProductId,
      costPrice,
      t,
      autoCalculate: true,
      shippingMode,
      initialConfig,
      onPricingChange: handlePricingChange,
    }),

    hasSimulation ? h("div", { className: `cjwiz-summary-card ${summaryStateClass}`, style: summaryStyle },
      h("div", { className: "cjwiz-summary-card__header" },
        pricing.destinationCountry ? h("span", { className: "cjwiz-badge cjwiz-badge--muted" },
          t("h_cjms_summary_primary", "Primary country: {country}", { country: pricing.destinationCountry })) : null,
        netProfit > 0
          ? h("span", { className: "cjwiz-summary-card__status" }, h(MdCheckCircle), t("h_cjwiz_summary_profitable", "Profitable"))
          : h("span", { className: "cjwiz-summary-card__status" }, h(MdCancel), t("h_cjwiz_summary_no_profit", "No Profit"))
      ),
      h("div", { className: "cjwiz-summary-card__grid" },
        h("div", null, h("span", null, t("h_cjwiz_summary_cj_cost", "CJ Product Cost")), h("strong", null, money(pricing.pricing.cjProductCost))),
        h("div", null, h("span", null, t("h_cjwiz_summary_shipping_cost", "Shipping Cost")), h("strong", null, money(pricing.pricing.cjShippingCost))),
        h("div", null, h("span", null, t("h_cjwiz_summary_total_supplier", "Total Supplier Cost")), h("strong", null, money(totalSupplierCost))),
        h("div", null, h("span", null, t("h_cjwiz_summary_payment_fees", "Payment Fees")), h("strong", null, money(pricing.pricing.stripeFee))),
        h("div", null, h("span", null, t("h_cjwiz_summary_elakay_fees", "Elakay Fees")), h("strong", null, money(pricing.pricing.elakayFee))),
        h("div", null, h("span", null, t("h_cjwiz_summary_total_fees", "Total Fees")), h("strong", null, money(pricing.pricing.totalFees))),
        h("div", null, h("span", null, t("h_cjwiz_summary_selling_price", "Selling Price")), h("strong", null, money(pricing.pricing.sellingPrice))),
        h("div", null, h("span", null, t("h_cjwiz_summary_discount", "Discount")), h("strong", null, money(discountAmount))),
        h("div", null, h("span", null, t("h_cjwiz_summary_customer_final", "Customer Final Price")), h("strong", null, money(pricing.pricing.customerTotal))),
        h("div", null, h("span", null, t("h_cjwiz_summary_profit", "Profit")), h("strong", null, money(netProfit))),
        h("div", null, h("span", null, t("h_cjwiz_summary_profit_margin", "Profit Margin")), h("strong", null, `${profitMarginDisplay}%`))
      ),
      netProfit <= 0 ? h("p", { className: "cjwiz-summary-card__hint" },
        t("h_cjwiz_summary_fix_hint", "Increase the selling price or lower costs -- Next stays disabled until profit is positive.")
      ) : null,
      losingCountries.length ? h("p", { className: "cjwiz-summary-card__hint" },
        t("h_cjms_losing_countries", "Not profitable in: {countries}. Raise that country's price or turn off selling there.", { countries: losingCountries.join(", ") })
      ) : null
    ) : null,

    h("div", { className: `cjwiz-step__status ${valid ? "cjwiz-step__status--ready" : ""}` },
      valid
        ? h("span", { className: "cjwiz-badge cjwiz-badge--success" }, t("h_cjwiz_step2_ready", "Pricing ready"))
        : discountInvalid
          ? h("span", { className: "cjds-field-error" }, t("h_cj_shipping_discount_min", "Minimum discount is 3% -- use 0 for no discount."))
          : h("span", { className: "cjds-muted" }, t(
              "h_cjwiz_step2_not_ready",
              "Waiting for a completed shipping quote and a profitable selling price."
            ))
    )
  );
};

module.exports = { CJShippingPricingStep };
